import { getCustomEventList } from 'client/js/common/selectors/DruidSelectors';
import { selectCurrency } from 'client/js/main/components/analytics/LightweightStickyBar/LightweightFilters.selectors';
import {
  DEFAULT_ACTIVE_MODE,
  ROI_ANALYTICS_CALCULATIONS,
  ROI_ANALYTICS_DATASOURCES,
} from 'client/js/main/screens/ScreenRoiHub/constants';
import type { ColumnOptions } from 'client/js/main/selectors/Analytics/createColumnsSelector.types';
import { createDynamicCalculationCustomEventColumnsSelector } from 'client/js/main/selectors/Analytics/getDymanicCalculationCustomEventColumns';
import { createDynamicCalculationColumnsSelector } from 'client/js/main/selectors/Analytics/getDynamicCalculationColumns';
import { buildDynamicEventList } from 'client/js/main/services/GroupedMetrics/GroupedMetrics.constants';
import type { RootState } from 'client/js/redux';
import { createSelector } from 'reselect';
import { ROI_ANALYTICS_NAMESPACE } from '../../../../../shared/constants';
import { ALL_GRANULARITY, ANALYTICS_CONTEXT, DAY_GRANULARITY } from '../../../../../shared/constants/analytics';
import { TOTAL_COUNT_METRIC, UNIQUE_COUNT_METRIC } from '../../../../../shared/constants/analytics/metrics';
import { CALCULATION_ID_EVENTS } from '../../../../../shared/constants/dynamicMetrics';
import { DATASOURCES, STANDARD_EVENT_NAMES } from '../../../common/support/eo_mapping';
import { regenerateSelectedColumns } from '../../../common/support/utils';
import type { RoiAnalyticsShareableData, ShareableData } from '../../../common/types/shareableTypes';
import { handleGetNewDateRangeInfo } from '../../support/Dates/dateUtils';
import type { NewDateRangeInfo } from '../../types/dateRangeTypes';
import {
  createCalculationColumnsSelector,
  createCustomEventColumnsSelector,
  createDatasourceColumnsSelector,
  createDimensionColumnsSelector,
  createStandardEventColumnsSelector,
} from '../Analytics';
import { createCustomCalculationColumnsSelector } from '../CustomCalculationSelectors';

const EMPTY_SHAREABLE = {} as ShareableData;
const EMPTY_NAMESPACE = {} as RoiAnalyticsShareableData;
const EMPTY_TABLE_STATE = {} as NonNullable<RoiAnalyticsShareableData[typeof ANALYTICS_CONTEXT.ROI_ANALYTICS_TABLE]>;
const EMPTY_TRENDS_STATE = {} as NonNullable<RoiAnalyticsShareableData[typeof ANALYTICS_CONTEXT.ADS_TRENDS]>;
const EMPTY_ARRAY = [];

const getShareableStateData = (state: RootState): ShareableData => state.shareable.data || EMPTY_SHAREABLE;

export const selectRoiAnalyticsShareableState: (state: RootState) => RoiAnalyticsShareableData = createSelector(
  getShareableStateData,
  (shareableStateData) => shareableStateData[ROI_ANALYTICS_NAMESPACE] || EMPTY_NAMESPACE
);

const selectRoiAnalyticsReportTableState = createSelector(
  selectRoiAnalyticsShareableState,
  (roiAnalyticsShareableState) => roiAnalyticsShareableState[ANALYTICS_CONTEXT.ROI_ANALYTICS_TABLE] || EMPTY_TABLE_STATE
);

const selectRoiAnalyticsTrendsState = createSelector(
  selectRoiAnalyticsShareableState,
  (roiAnalyticsShareableState) => roiAnalyticsShareableState[ANALYTICS_CONTEXT.ADS_TRENDS] || EMPTY_TRENDS_STATE
);

export const selectRoiTrendsGranularity = createSelector(
  selectRoiAnalyticsTrendsState,
  (trendsState) => trendsState?.granularity || DAY_GRANULARITY
);

export const selectReportGranularity = createSelector(selectRoiAnalyticsReportTableState, (reportTableState) => {
  return reportTableState?.granularity || ALL_GRANULARITY;
});

export const selectReportActiveMode = createSelector(selectRoiAnalyticsReportTableState, (reportTableState) => {
  return reportTableState?.activeMode || DEFAULT_ACTIVE_MODE;
});

export const selectReportMetric = createSelector(selectRoiAnalyticsReportTableState, (reportTableState) => {
  return reportTableState?.unique ? UNIQUE_COUNT_METRIC : TOTAL_COUNT_METRIC;
});

const getReportQuery = createSelector(selectRoiAnalyticsReportTableState, (reportTableState) => {
  return reportTableState?.query;
});

const getReportDimensions = createSelector(getReportQuery, (reportQuery) => {
  return reportQuery?.dimensions || EMPTY_ARRAY;
});

const getDatasources = () => ROI_ANALYTICS_DATASOURCES;

const getCalculations = () => ROI_ANALYTICS_CALCULATIONS;

const getEvents = () => Object.keys(STANDARD_EVENT_NAMES);

const getDynamicCalculations = () => Object.keys(CALCULATION_ID_EVENTS);

const getColumnOptions = createSelector(
  (state: RootState) => selectCurrency(state, ROI_ANALYTICS_NAMESPACE),
  (currencySwitcherCode: string): ColumnOptions => {
    return { localCurrency: currencySwitcherCode, extensions: { CURRENCY_SWITCHER: true } };
  }
);

export const selectNewDateRangeInfo = createSelector(selectRoiAnalyticsShareableState, (roiAnalyticsShareableState) => {
  const { dateRange, defaultRange } = roiAnalyticsShareableState as NewDateRangeInfo;
  return handleGetNewDateRangeInfo({
    dateRange,
    defaultRange,
  });
});

export const selectDimensionColumns = createDimensionColumnsSelector(getReportDimensions);
export const selectDatasourceColumns = createDatasourceColumnsSelector(getDatasources, getColumnOptions);
export const selectCalculationColumns = createCalculationColumnsSelector(getCalculations, getColumnOptions);
export const selectStandardEventColumns = createStandardEventColumnsSelector(getEvents, getColumnOptions);
export const selectCustomEventColumns = createCustomEventColumnsSelector();
export const selectDynamicEventColumns = createDynamicCalculationColumnsSelector(buildDynamicEventList, getColumnOptions);
export const selectCustomCalculationEventColumns = createCustomCalculationColumnsSelector(getColumnOptions);
export const selectDynamicCalculationCustomEventColumns = createDynamicCalculationCustomEventColumnsSelector(
  getDynamicCalculations,
  getCustomEventList,
  getColumnOptions
);

export const selectAllColumns = createSelector(
  [
    selectDimensionColumns,
    selectDatasourceColumns,
    selectCalculationColumns,
    selectStandardEventColumns,
    selectCustomEventColumns,
    selectCustomCalculationEventColumns,
    selectDynamicEventColumns,
    selectDynamicCalculationCustomEventColumns,
  ],
  (
    dimensionColumns,
    datasourceColumns,
    calculationColumns,
    eventColumns,
    customEventColumns,
    customCalculationColumns,
    dynamicCalculationColumns,
    dynamicCalculationCustomEventColumns
  ) => [
    ...dimensionColumns,
    ...datasourceColumns,
    ...calculationColumns,
    ...eventColumns,
    ...customEventColumns,
    ...customCalculationColumns,
    ...dynamicCalculationColumns,
    ...dynamicCalculationCustomEventColumns,
  ]
);

export const selectSelectedColumns = createSelector(
  selectRoiAnalyticsShareableState,
  selectAllColumns,
  (roiAnalyticsShareableState, allColumns) => {
    return regenerateSelectedColumns(roiAnalyticsShareableState.selectedColumns, allColumns);
  }
);

export const selectDatasourcesForFilter = createSelector(getShareableStateData, () => Object.keys(DATASOURCES));
